import { createSlice, createAsyncThunk, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { ContentService } from "../../services/content";
import { Content, VideoContent, ContentSearchParams } from "../../types/content";
import { RootState } from "../";
import { CONTENT_TYPES } from "../../constants/content";

interface ContentTypeState {
  items: Content[];
  loading: boolean;
  error: string | null;
  total: number;
}

interface ContentState {
  regular: ContentTypeState;
  shorts: ContentTypeState;
  currentVideo: {
    data: VideoContent | null;
    loading: boolean;
    error: string | null;
  };
}

const initialTypeState: ContentTypeState = {
  items: [] as Content[],
  loading: false,
  error: null,
  total: 0,
};

const initialState: ContentState = {
  regular: { ...initialTypeState },
  shorts: { ...initialTypeState },
  currentVideo: {
    data: null,
    loading: false,
    error: null,
  },
};

// Helper function to validate content structure
const isValidContent = (data: any): data is Content => {
  // Handle nested content structure
  const content = data?.content || data;

  return (
    content &&
    typeof content === 'object' &&
    'id' in content &&
    'type' in content &&
    'title' in content &&
    typeof content.id === 'string' &&
    typeof content.type === 'string'
  );
};

// Helper function to extract content from response
const extractContent = (data: any): any => {
  return data?.content || data;
};

// Helper function to validate video content
const isVideoContent = (content: Content): content is VideoContent => {
  if (!content || !content.type) {
    return false;
  }
  return content.type === CONTENT_TYPES.VIDEO || content.type === CONTENT_TYPES.LIVE;
};

export const fetchContent = createAsyncThunk(
  "content/fetchContent",
  async (params: ContentSearchParams & { contentType?: 'regular' | 'shorts' }, { rejectWithValue }) => {
    try {
      const service = ContentService.getInstance();
      const response = await service.searchContent(params);

      // Extract the results array from the response
      const validData = response.results
        .map(extractContent)
        .filter(isValidContent);

      if (validData.length !== response.results.length) {
        console.warn('Some content items were invalid and filtered out');
      }

      return { 
        data: validData,
        contentType: params.contentType || 'regular' 
      };
    } catch (error) {
      console.error('Error fetching content:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch content');
    }
  }
);

export const fetchVideoById = createAsyncThunk(
  "content/fetchVideoById",
  async (id: string, { rejectWithValue }) => {
    try {
      const service = ContentService.getInstance();
      const response = await service.getContentById(id);
      
      // Extract content from response
      const data = extractContent(response);
      
      // Validate response data
      if (!isValidContent(data)) {
        throw new Error('errors.somethingWentWrong');
      }

      if (!isVideoContent(data)) {
        throw new Error('errors.pageNotFound');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('errors.somethingWentWrong');
    }
  }
);

const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {
    clearCurrentVideo: (state) => {
      state.currentVideo = initialState.currentVideo;
    },
    clearContentList: (state, action: PayloadAction<'regular' | 'shorts'>) => {
      state[action.payload] = { ...initialTypeState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContent.pending, (state, action) => {
        const contentType = action.meta.arg.contentType || 'regular';
        state[contentType] = {
          ...state[contentType],
          loading: true,
          error: null
        };
      })
      .addCase(fetchContent.fulfilled, (state, action) => {
        const { data, contentType } = action.payload;
        state[contentType] = {
          items: data,
          loading: false,
          total: data.length,
          error: null
        };
      })
      .addCase(fetchContent.rejected, (state, action) => {
        const contentType = action.meta.arg.contentType || 'regular';
        state[contentType] = {
          ...state[contentType],
          loading: false,
          error: action.payload as string,
          items: []
        };
      })
      .addCase(fetchVideoById.pending, (state) => {
        state.currentVideo.loading = true;
        state.currentVideo.error = null;
      })
      .addCase(fetchVideoById.fulfilled, (state, action) => {
        state.currentVideo.loading = false;
        state.currentVideo.data = action.payload;
        state.currentVideo.error = null;
      })
      .addCase(fetchVideoById.rejected, (state, action) => {
        state.currentVideo.loading = false;
        state.currentVideo.error = action.payload as string;
        state.currentVideo.data = null;
      });
  },
});

// Selectors
const selectContentState = (state: RootState) => state.content;

export const selectRegularVideos = createSelector(
  [selectContentState],
  (content) => {
    const items = content?.regular?.items || [];
    return Array.isArray(items) 
      ? items.filter((item): item is VideoContent => isValidContent(item) && isVideoContent(item))
      : [];
  }
);

export const selectRegularLoading = (state: RootState) => 
  state.content?.regular?.loading || false;

export const selectRegularError = (state: RootState) => 
  state.content?.regular?.error || null;

export const selectShorts = createSelector(
  [selectContentState],
  (content) => {
    const items = content?.shorts?.items || [];
    return Array.isArray(items) 
      ? items.filter((item): item is VideoContent => isValidContent(item) && isVideoContent(item))
      : [];
  }
);

export const selectShortsLoading = (state: RootState) => 
  state.content?.shorts?.loading || false;

export const selectShortsError = (state: RootState) => 
  state.content?.shorts?.error || null;

export const selectCurrentVideo = (state: RootState) => 
  state.content?.currentVideo?.data || null;

export const selectCurrentVideoLoading = (state: RootState) => 
  state.content?.currentVideo?.loading || false;

export const selectCurrentVideoError = (state: RootState) => 
  state.content?.currentVideo?.error || null;

export const { clearCurrentVideo, clearContentList } = contentSlice.actions;

export default contentSlice.reducer; 